import { motion } from "framer-motion";

const nav = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

const stats = [
  { v: "500+", l: "Parts In Stock" },
  { v: "10K+", l: "Customers" },
  { v: "24 hr", l: "Fast Dispatch" },
  { v: "99%",  l: "Satisfaction" },
];

export default function Hero() {
  return (
    <section id="home" className="relative min-h-screen overflow-hidden bg-[#0B1120]" aria-label="Hero">
      {/* Background photo */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=1800&q=80')" }}
        aria-hidden
      />
      {/* Dark overlay — heavier on left where text sits */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0B1120]/95 via-[#0B1120]/80 to-[#0B1120]/55" aria-hidden />
      {/* Extra universal dim */}
      <div className="absolute inset-0 bg-[#0B1120]/30" aria-hidden />
      {/* Red glow accent */}
      <div className="absolute -bottom-32 -left-32 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(227,24,55,0.10) 0%, transparent 70%)" }} aria-hidden />
      {/* Bottom pinstripe */}
      <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-red-600/50 to-transparent" />

      <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 min-h-screen flex items-center">
        <div className="w-full max-w-2xl py-32">

          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="inline-flex items-center gap-2 bg-red-600/20 border border-red-500/40 text-red-400
                       text-[11px] font-bold uppercase tracking-[0.16em] px-4 py-2 rounded-full mb-7"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
            Trusted by 10,000+ Drivers Across the USA
          </motion.div>

          {/* H1 — 34px base, responsive up */}
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, lineHeight: 1.15 }}
            className="text-[34px] sm:text-[42px] md:text-[50px] text-white tracking-tight mb-5"
          >
            Quality Auto Parts,
            <br />
            <span className="text-red-500">Delivered Fast.</span>
          </motion.h1>

          {/* Sub */}
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.2 }}
            className="text-white/55 text-base sm:text-lg leading-relaxed max-w-lg mb-9"
          >
            Genuine OEM-certified components — engine, brakes, ignition, lighting and more —
            shipped nationwide within <span className="text-white/80 font-semibold">24 hours</span>.
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.28 }}
            className="flex flex-wrap gap-3 mb-14"
          >
            <button onClick={() => nav("#products")}
              className="group flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white font-bold
                         px-7 py-3.5 rounded-2xl text-sm transition-all duration-300
                         shadow-2xl shadow-red-900/50 hover:shadow-red-600/60 hover:-translate-y-0.5"
              style={{ fontFamily: "'Syne', sans-serif" }}>
              Browse Products
              <svg className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" fill="none"
                viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
              </svg>
            </button>

            <button onClick={() => nav("#contact")}
              className="border border-white/25 hover:border-white/55 text-white/70 hover:text-white
                         font-semibold px-7 py-3.5 rounded-2xl text-sm transition-all duration-300 backdrop-blur-sm">
              Contact Us
            </button>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.45 }}
            className="grid grid-cols-4 gap-4 border-t border-white/10 pt-8"
          >
            {stats.map(s => (
              <div key={s.l}>
                <div className="text-2xl text-white leading-none mb-1"
                  style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800 }}>{s.v}</div>
                <div className="text-white/38 text-[11px] leading-tight">{s.l}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 cursor-pointer"
        onClick={() => nav("#products")} role="button" aria-label="Scroll to products"
      >
        <span className="text-white/30 text-[10px] uppercase tracking-[0.2em]">Scroll</span>
        <motion.div animate={{ y: [0, 6, 0] }} transition={{ duration: 1.6, repeat: Infinity }}
          className="w-5 h-8 border border-white/20 rounded-full flex items-start justify-center pt-1.5">
          <div className="w-1 h-2 bg-red-500 rounded-full" />
        </motion.div>
      </motion.div>
    </section>
  );
}
